import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import URLs from '../../../../../utils/appURLs';
import { updateToast } from '../../../../../common/redux/reducer/commonSlices/toastSlice';
import axiosInstance from '../../../../../app/config/axiosInstance';

type ReturnedType = any;

export interface Customer {
  firstName: string;
  lastName: string;
  contactNumber: string;
  deliveryAddress: string;
  deliverableLat: number;
  deliverableLng: number;
  customerId?: string;
  tempCustomer: boolean;
}

type ThunkArgs = { updatePayload: Customer; customerID: string };

type AddCustomer = { data: Customer; pageNo: number; pageSize: number };

type initialStateProps = {
  isLoading: boolean;
  type: 'customers' | 'save-customer' | 'update-customer' | 'delete-customer';
  data: Customer[] | [];
  error: string | null;
  count: number | null;
};

const initialState: initialStateProps = {
  isLoading: false,
  type: 'customers',
  data: [],
  error: null,
  count: null
};

// Fetch customers
export const getCustomersAction = createAsyncThunk<any, any>(
  '/customer/all',
  async (data, thunkApi) => {
    const { pageNo, pageSize } = data;
    try {
      const response = await axiosInstance({
        method: 'GET',
        url: `${URLs.GET_CUSTOMER}pageno=${pageNo}&pagesize=${pageSize}`
      });
      return response?.data;
    } catch (error: any) {
      return thunkApi.rejectWithValue(error);
    }
  }
);

// Add customer
export const addCustomer = createAsyncThunk<ReturnedType, AddCustomer>(
  'customer/add',
  async (params, thunkApi) => {
    const { data, pageNo, pageSize } = params;
    try {
      const response = await axiosInstance({
        method: 'POST',
        url: `${URLs.ADD_CUSTOMER}`,
        data
      });
      await thunkApi.dispatch(getCustomersAction({ pageNo, pageSize }));
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: response?.data?.message,
          severity: 'success'
        })
      );
      return response?.data?.data;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error?.response?.data?.error || 'Something went wrong',
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error);
    }
  }
);

// Update customer
export const updateCustomer = createAsyncThunk<ReturnedType, any>(
  'update/customer',
  async (data, thunkApi) => {
    const { customerId, updatePayload, pageNo, pageSize } = data;
    try {
      const response = await axiosInstance({
        method: 'PUT',
        url: `${URLs.UPDATE_CUSTOMER}?customerId=${customerId}`,
        data: updatePayload
      });
      await thunkApi.dispatch(getCustomersAction({ pageNo, pageSize }));
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: response?.data?.message,
          severity: 'success'
        })
      );
      return response?.data;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error?.response?.data?.error,
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error);
    }
  }
);

// Delete customer
export const deleteCustomer = createAsyncThunk<ReturnedType, any>(
  'delete/customer',
  async (data, thunkApi) => {
    const { customerId, pageNo, pageSize } = data;
    try {
      const response = await axiosInstance({
        method: 'DELETE',
        url: `${URLs.DELETE_CUSTOMER}?customerId=${customerId}`
      });
      await thunkApi.dispatch(getCustomersAction({ pageNo, pageSize }));
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: response?.data?.message,
          severity: 'success'
        })
      );
      return response?.data?.data;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error?.response?.data?.error,
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error);
    }
  }
);

const customerAddSlice = createSlice({
  name: 'customer',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      // Add customer:
      .addCase(addCustomer.pending, state => {
        state.isLoading = true;
      })
      .addCase(addCustomer.fulfilled, (state, action: PayloadAction<ReturnedType>) => {
        state.data = action?.payload?.data;
        state.isLoading = false;
        state.error = null;
      })
      .addCase(addCustomer.rejected, (state, action: any) => {
        state.error = action.error.message || 'Something went wrong';
        state.isLoading = false;
      });
  }
});

const customerGetSlice = createSlice({
  name: 'customer',
  initialState,
  reducers: {
    clearCustomersAction: state => {
      state.data = [];
    }
  },
  extraReducers: builder => {
    builder
      .addCase(getCustomersAction.pending, state => {
        state.isLoading = true;
        state.data = [];
      })
      .addCase(getCustomersAction.fulfilled, (state, action: PayloadAction<any>) => {
        state.isLoading = false;
        const {
          data: { updateClientCustomerRequests, count }
        } = action.payload;
        state.data = updateClientCustomerRequests?.map((item: any) => ({
          ...item,
          id: item?.id,
          firstName: item?.firstName,
          lastName: item?.lastName,
          contactNumber: item?.contactNumber,
          deliveryAddress: item?.deliveryAddress
        }));
        state.count = count;
        state.error = null;
      })
      .addCase(getCustomersAction.rejected, (state, action: any) => {
        state.isLoading = false;
        state.data = [];
        state.error = action.payload?.response?.data.error || 'Something went wrong';
      });
  }
});

const customerUpdateSlice = createSlice({
  name: 'customer',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(updateCustomer.pending, state => {
        state.isLoading = true;
        state.type = 'update-customer';
      })
      .addCase(updateCustomer.fulfilled, (state, action: PayloadAction<ReturnedType>) => {
        state.isLoading = false;
        // Replace the updated customer in the data array
        const updatedData = state.data.map(customer =>
          customer.customerId === action.payload?.customerId ? action.payload : customer
        );
        state.data = updatedData;
        state.error = null;
      })
      .addCase(updateCustomer.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload?.response?.data?.error || 'Something went wrong';
      });
  }
});

const customerDeleteSlice = createSlice({
  name: 'customer',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(deleteCustomer.pending, state => {
        state.isLoading = true;
        state.data = [];
      })
      .addCase(deleteCustomer.fulfilled, (state, action: PayloadAction<ReturnedType>) => {
        state.isLoading = false;
        state.data = action.payload;
        state.error = null;
      })
      .addCase(deleteCustomer.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action?.payload?.response?.data?.error || 'Something went wrong';
      });
  }
});

export const customerAddReducer = customerAddSlice.reducer;
export const customerGetReducer = customerGetSlice.reducer;
export const customerUpdateReducer = customerUpdateSlice.reducer;
export const customerDeleteReducer = customerDeleteSlice.reducer;
export const { clearCustomersAction } = customerGetSlice.actions;
